import { ArrowDown, ArrowUp, ChevronsUpDown, Inbox } from "lucide-react";
import type { ReactNode } from "react";

import { StatusPill } from "@/components/status-pill";
import type { AdminActivityLogEntry } from "@/lib/api";
import { cn } from "@/lib/utils";

export type SortDir = "asc" | "desc";

interface Props {
  entries: AdminActivityLogEntry[];
  searchTerm: string;
  sortBy: string;
  sortDir: SortDir;
  onSort: (col: string) => void;
  loading?: boolean;
}

/** Wrap every case-insensitive match of `term` in <mark> for search highlight. */
function highlight(text: string, term: string): ReactNode {
  if (!term) {
    return text;
  }
  const lower = text.toLowerCase();
  const q = term.toLowerCase();
  const parts: ReactNode[] = [];
  let i = 0;
  let key = 0;
  for (let next = lower.indexOf(q); next !== -1; next = lower.indexOf(q, i)) {
    parts.push(text.slice(i, next));
    parts.push(
      <mark key={key++} className="bg-accent/20 rounded px-0.5">
        {text.slice(next, next + term.length)}
      </mark>,
    );
    i = next + term.length;
  }
  parts.push(text.slice(i));
  return <>{parts}</>;
}

const COLUMNS: { key: string; label: string; sortable: boolean; right?: boolean }[] = [
  { key: "email", label: "User", sortable: true },
  { key: "action", label: "Action", sortable: true },
  { key: "detail", label: "Detail", sortable: false },
  { key: "created_at", label: "When", sortable: true, right: true },
];

export function ActivityTable({ entries, searchTerm, sortBy, sortDir, onSort, loading }: Props) {
  return (
    <div className="bg-card border-border relative overflow-hidden rounded-lg border shadow-sm">
      {loading && (
        <div className="bg-background/60 absolute inset-0 z-10 flex items-center justify-center">
          <span className="text-muted text-xs">Loading…</span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-warm-gray-01 border-border border-b">
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    "text-muted px-4 py-3 text-[10px] font-medium tracking-[0.16em] uppercase",
                    col.right && "text-right",
                  )}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className={cn(
                        "hover:text-foreground inline-flex items-center gap-1 transition-colors",
                        col.right && "flex-row-reverse",
                      )}
                    >
                      {col.label}
                      {sortBy === col.key ? (
                        sortDir === "asc" ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : (
                          <ArrowDown className="h-3 w-3" />
                        )
                      ) : (
                        <ChevronsUpDown className="h-3 w-3 opacity-60" />
                      )}
                    </button>
                  ) : (
                    col.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-border divide-y">
            {entries.length === 0 ? (
              <tr>
                <td colSpan={COLUMNS.length}>
                  <div className="flex flex-col items-center justify-center gap-1.5 px-6 py-12 text-center">
                    <Inbox className="text-muted h-6 w-6" />
                    <p className="text-foreground text-sm font-medium">No activity found</p>
                    <p className="text-muted max-w-xs text-xs">
                      Try widening the filters or clearing the search above.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              entries.map((entry) => {
                const name = `${entry.first_name ?? ""} ${entry.last_name ?? ""}`.trim();
                return (
                  <tr key={entry.id} className="hover:bg-warm-gray-01 transition-colors">
                    <td className="px-4 py-3 align-middle">
                      <div className="text-foreground font-medium">
                        {name ? highlight(name, searchTerm) : "—"}
                      </div>
                      <div className="text-muted text-xs">
                        {highlight(entry.email ?? entry.user_id, searchTerm)}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <StatusPill>{highlight(entry.action, searchTerm)}</StatusPill>
                    </td>
                    <td className="text-muted px-4 py-3 align-middle">
                      {entry.detail ? highlight(entry.detail, searchTerm) : "—"}
                    </td>
                    <td className="text-muted px-4 py-3 text-right align-middle whitespace-nowrap">
                      {new Date(entry.created_at).toLocaleString()}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
